import { useWorkspace } from '../workspace/WorkspaceProvider';
import { isReadOnlyMember } from '../api/types';

/**
 * Whether the signed-in user may write in the active workspace — the single switch
 * behind every create/edit/delete affordance in the portal (`steward-viewer` read-only
 * enforcement).
 *
 * UX gating only. appcore checks capabilities on every write, so a viewer who reaches a
 * write path anyway (a stale tab, a hand-crafted request) still gets a 403. What this
 * buys is a portal that doesn't offer actions it knows will fail.
 *
 * Note it gates *workspace data*. A viewer keeps full control of things that are theirs:
 * their profile and preferences, AI consent, submitting feedback, accepting invites.
 * Those call sites deliberately do not consult this hook.
 */
export function useCanWrite(): boolean {
  const { activeMembership, meLoaded } = useWorkspace();

  // Still resolving GET /me. Withhold write controls for the moment rather than
  // offering an action that vanishes a beat later once we learn the user is a viewer;
  // the pages themselves are in their own loading state at this point.
  if (!meLoaded) return false;

  // /me settled but yielded no membership for the active workspace — a failed /me, or a
  // session running against the façade's fallback workspace (WorkspaceProvider keeps the
  // portal usable in that case rather than dead-ending). We cannot identify the user as
  // a viewer, so fail open and leave today's behavior intact.
  //
  // Note this is the OPPOSITE choice to RequireAdmin, which renders nothing in the same
  // state. The asymmetry is deliberate: RequireAdmin guards a whole route, where showing
  // an admin surface to a non-admin is the costly error; here the cost runs the other way
  // — a writer stripped of every control is broken, while a viewer shown a control just
  // meets the 403 appcore was always going to return. Don't "fix" one to match the other.
  if (!activeMembership) return true;

  return !isReadOnlyMember(activeMembership.roleIds);
}
